// Synchronizacja postępu jednego profilu z serwerem. Jedno żądanie `PUT /api/progress` robi naraz dwie rzeczy: wysyła kopię z urządzenia
// i odbiera kopię scaloną z serwerową (patrz src/lib/progressMerge.ts), więc „pobierz” i „wyślij” to ten sam przebieg.
//
//  • Po każdej zmianie postępu (`touch`) kopia jest oznaczana jako „brudna” (zapis w pamięci przeglądarki) i po krótkiej chwili wysyłana.
//    Znacznik przeżywa zamknięcie karty, więc zmiany z offline trafią na serwer przy następnym otwarciu aplikacji.
//  • Gdy sieć lub serwer zawodzą, ponawiamy z rosnącą przerwą (a także po powrocie do karty i po odzyskaniu internetu — to robi wywołujący).
//  • Odpowiedź scala się z tym, co dziecko zrobiło w trakcie lotu żądania; nic nie ginie.
//  • Gdy rodzic wyzerował postęp (409 „reset”), stara kopia z urządzenia jest porzucana, a nie odsyłana z powrotem.
import { mergeProgress, validateProgress } from './progressMerge.ts';
import type { ProfilesClient } from './profilesApi.ts';
import type { ProfileStore } from './profileStore.ts';
import type { Progress } from '../types.ts';

export type SyncState =
  /** Jeszcze nie próbowaliśmy. */
  | 'idle'
  | 'syncing'
  /** Serwer ma wszystko, co jest na urządzeniu. */
  | 'synced'
  /** Dziecko coś zrobiło; za chwilę wyślemy (krótka zwłoka na koniec serii zmian). */
  | 'pending'
  /** Są zmiany, których serwer nie ma, a nie ma z nim łączności. */
  | 'offline'
  /** Serwer odpowiada błędem (np. brak migracji bazy). */
  | 'error'
  /** Profil został zarchiwizowany albo usunięty przez rodzica. */
  | 'gone'
  /** Serwer nie zna klucza tego urządzenia. */
  | 'unpaired';

export interface SyncEvent {
  state: SyncState;
  /** Kopia na urządzeniu została zmieniona odpowiedzią serwera (np. dane z drugiego urządzenia) — ekran może się odświeżyć. */
  changed: boolean;
  /** Rodzic wyzerował postęp: kopia z urządzenia została zastąpiona pustą. */
  reset: boolean;
}

export interface Timers {
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export interface SyncDeps {
  profileId: string;
  client: Pick<ProfilesClient, 'putProgress'>;
  store: ProfileStore;
  /** Bieżąca kopia postępu tego profilu w pamięci aplikacji. */
  get(): Progress;
  /** Podmienia kopię w pamięci aplikacji (po scaleniu z serwerem). Zapis do przeglądarki robi sama synchronizacja. */
  replace(p: Progress): void;
  now?: () => Date;
  timers?: Timers;
  /** Po tylu ms od ostatniej zmiany wysyłamy (kolejne zmiany w tym czasie idą jednym żądaniem). */
  debounceMs?: number;
  /** Kolejne przerwy między ponowieniami po błędzie; ostatnia powtarza się w nieskończoność. */
  retryMs?: readonly number[];
}

export interface Sync {
  state(): SyncState;
  /** Opis błędu z serwera (dla rodzica) albo ''. */
  detail(): string;
  /** Kiedy serwer ostatnio potwierdził zapis (ISO) albo null. */
  syncedAt(): string | null;
  /** Postęp zmienił się lokalnie: oznacz jako niewysłany i zaplanuj wysyłkę. */
  touch(): void;
  /** Wyślij i odbierz teraz. Wiele wywołań naraz = jeden przebieg. Zwraca stan po zakończeniu. */
  syncNow(): Promise<SyncState>;
  subscribe(fn: (e: SyncEvent) => void): () => void;
  /** Kończy pracę (profil przestaje być aktywny): zatrzymuje liczniki; trwające żądanie dokończy zapis do przeglądarki, ale nikogo nie powiadomi. */
  stop(): void;
}

const DEFAULT_RETRY_MS = [10_000, 30_000, 60_000, 120_000, 300_000] as const;
const MAX_LOOPS = 3;

const defaultTimers: Timers = {
  set(fn, ms) {
    const handle = setTimeout(fn, ms);
    (handle as { unref?: () => void }).unref?.(); // w Node (testy) licznik nie trzyma procesu przy życiu; w przeglądarce to zwykła liczba
    return handle;
  },
  clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
};
/** Czy to ten sam postęp — liczy się treść, nie kolejność kluczy (kopia z serwera jest kanoniczna, a lokalna nie musi być). */
const same = (a: Progress, b: Progress): boolean => {
  const ca = validateProgress(a);
  const cb = validateProgress(b);
  return ca !== null && cb !== null && JSON.stringify(ca) === JSON.stringify(cb);
};

export function createSync(deps: SyncDeps): Sync {
  const { profileId: id, client, store } = deps;
  const now = deps.now ?? (() => new Date());
  const timers = deps.timers ?? defaultTimers;
  const debounceMs = deps.debounceMs ?? 1500;
  const retryMs = deps.retryMs?.length ? deps.retryMs : DEFAULT_RETRY_MS;

  let state: SyncState = 'idle';
  let detail = '';
  let version = 0; // rośnie z każdą zmianą lokalną; po powrocie żądania wiemy, czy w międzyczasie coś doszło
  let stopped = false;
  let running: Promise<SyncState> | null = null;
  let again = false;
  let failures = 0;
  let debounceTimer: unknown = null;
  let retryTimer: unknown = null;
  const listeners = new Set<(e: SyncEvent) => void>();

  const emit = (changed: boolean, reset: boolean): void => {
    if (stopped) return;
    for (const fn of [...listeners]) fn({ state, changed, reset });
  };
  const setState = (s: SyncState, d = ''): void => {
    state = s;
    detail = d;
  };
  const clearTimers = (): void => {
    if (debounceTimer !== null) timers.clear(debounceTimer);
    if (retryTimer !== null) timers.clear(retryTimer);
    debounceTimer = retryTimer = null;
  };
  const scheduleRetry = (): void => {
    if (stopped || retryTimer !== null) return;
    const wait = retryMs[Math.min(failures - 1, retryMs.length - 1)] as number;
    retryTimer = timers.set(() => {
      retryTimer = null;
      void api.syncNow();
    }, wait);
  };

  /** Jeden przebieg: wysyła kopię, odbiera scaloną i zapisuje ją. Zwraca true, gdy trzeba od razu powtórzyć (doszły zmiany w trakcie). */
  async function cycle(): Promise<boolean> {
    const sentVersion = version;
    const sent = deps.get();
    const meta = store.syncMeta(id);
    setState('syncing');
    emit(false, false);
    const r = await client.putProgress(id, sent, meta.epoch);
    const stamp = now().toISOString();
    const moved = version !== sentVersion; // dziecko skończyło coś, zanim serwer odpowiedział

    if (r.kind === 'ok' || r.kind === 'reset') {
      failures = 0;
      const current = deps.get();
      const next = r.kind === 'ok' && moved ? mergeProgress(r.progress, current) : r.progress;
      const changed = !same(next, current);
      store.saveProgress(id, next);
      store.setSyncMeta(id, { epoch: r.epoch, dirty: r.kind === 'ok' && moved, syncedAt: stamp });
      if (changed) deps.replace(next);
      setState('synced');
      emit(changed, r.kind === 'reset');
      return r.kind === 'ok' && moved;
    }

    if (r.kind === 'gone' || r.kind === 'unpaired') {
      setState(r.kind);
      emit(false, false);
      return false;
    }

    failures++;
    setState(r.kind === 'offline' ? 'offline' : 'error', r.kind === 'error' ? r.detail : '');
    emit(false, false);
    scheduleRetry();
    return false;
  }

  const api: Sync = {
    state: () => state,
    detail: () => detail,
    syncedAt: () => store.syncMeta(id).syncedAt,

    touch() {
      if (stopped) return;
      version++;
      if (state === 'idle' || state === 'synced') {
        setState('pending'); // znaczek „zapisane na serwerze” nie może świecić, gdy są zmiany, których serwer jeszcze nie ma
        emit(false, false);
      }
      const meta = store.syncMeta(id);
      if (!meta.dirty) store.setSyncMeta(id, { ...meta, dirty: true });
      if (debounceTimer !== null) timers.clear(debounceTimer);
      debounceTimer = timers.set(() => {
        debounceTimer = null;
        if (store.syncMeta(id).dirty) void api.syncNow(); // w międzyczasie mogło wysłać to inne wywołanie (np. powrót do karty)
      }, debounceMs);
    },

    syncNow() {
      if (stopped) return Promise.resolve(state);
      clearTimers(); // wysyłamy teraz, więc oczekiwanie na koniec serii zmian i ponowienie po błędzie są zbędne (zmiana w locie ustawi je na nowo)
      if (running) {
        again = true; // trwa przebieg: po nim jeszcze jeden, żeby objął to, co doszło
        return running;
      }
      running = (async () => {
        try {
          for (let i = 0; i < MAX_LOOPS; i++) {
            again = false;
            const repeat = await cycle();
            if (!repeat && !again) break;
            if (state !== 'synced') break;
          }
          return state;
        } finally {
          running = null;
        }
      })();
      return running;
    },

    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },

    stop() {
      stopped = true;
      clearTimers();
      listeners.clear();
    },
  };
  return api;
}
